/**
 * JWT session management utilities
 */

import { SESSION_CONFIG } from '../config/google.js';

/**
 * Create simple token for authenticated user (base64 encoded for client-side only)
 * Note: This is not secure for production - use server-side JWT signing
 */
export const createJWT = (payload) => {
  const tokenPayload = {
    ...payload,
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + (8 * 60 * 60), // 8 hours
  };

  // Base64 encode for simple client-side storage (NOT secure for production)
  return btoa(JSON.stringify(tokenPayload));
};

/**
 * Verify and decode token
 */
export const verifyJWT = (token) => {
  try {
    // Base64 decode for simple client-side verification (NOT secure for production)
    const payload = JSON.parse(atob(token));
    return payload;
  } catch (error) {
    console.error('Token verification failed:', error.message);
    return null;
  }
};

/**
 * Store authentication token securely
 */
export const storeAuthToken = (token) => {
  try {
    localStorage.setItem(SESSION_CONFIG.storageKey, token);
    return true;
  } catch (error) {
    console.error('Failed to store auth token:', error);
    return false;
  }
};

/**
 * Retrieve authentication token from storage
 */
export const getAuthToken = () => {
  try {
    return localStorage.getItem(SESSION_CONFIG.storageKey);
  } catch (error) {
    console.error('Failed to retrieve auth token:', error);
    return null;
  }
};

/**
 * Remove authentication token from storage
 */
export const clearAuthToken = () => {
  try {
    localStorage.removeItem(SESSION_CONFIG.storageKey);
    sessionStorage.clear(); // Clear any OAuth state data
    return true;
  } catch (error) {
    console.error('Failed to clear auth token:', error);
    return false;
  }
};

/**
 * Check if token is valid and not expired
 */
export const isTokenValid = (token = null) => {
  const tokenToCheck = token || getAuthToken();
  
  if (!tokenToCheck) {
    return false;
  }

  const decoded = verifyJWT(tokenToCheck);
  if (!decoded) {
    return false;
  }

  // Check if token is expired
  const currentTime = Math.floor(Date.now() / 1000);
  if (decoded.exp <= currentTime) {
    return false;
  }

  return true;
};

/**
 * Check if token needs refresh (within refresh threshold)
 */
export const shouldRefreshToken = (token = null) => {
  const tokenToCheck = token || getAuthToken();
  
  if (!tokenToCheck) {
    return false;
  }

  const decoded = verifyJWT(tokenToCheck);
  if (!decoded) {
    return false;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = decoded.exp - currentTime;
  const refreshThresholdSeconds = SESSION_CONFIG.refreshThreshold / 1000;

  return timeUntilExpiry <= refreshThresholdSeconds;
};

/**
 * Decode token payload without verification (for getting user info)
 */
export const decodeTokenPayload = (token = null) => {
  const tokenToCheck = token || getAuthToken();
  
  if (!tokenToCheck) {
    return null;
  }

  try {
    // Decode to get payload
    const decoded = JSON.parse(atob(tokenToCheck));
    return decoded;
  } catch (error) {
    console.error('Failed to decode token payload:', error);
    return null;
  }
};

/**
 * Get current user session information
 */
export const getCurrentSession = () => {
  const token = getAuthToken();
  
  if (!isTokenValid(token)) {
    return null;
  }

  const payload = decodeTokenPayload(token);
  if (!payload) {
    return null;
  }

  return {
    user: {
      id: payload.sub,
      email: payload.email,
      name: payload.name,
      picture: payload.picture
    },
    role: payload.role,
    permissions: payload.permissions || [],
    isAuthenticated: true,
    expiresAt: payload.exp * 1000, // Convert to milliseconds
    needsRefresh: shouldRefreshToken(token)
  };
};

/**
 * Refresh user session with new token data
 */
export const refreshSession = async (newTokenData) => {
  try {
    // Create new JWT with updated information
    const newToken = createJWT({
      sub: newTokenData.user.id,
      email: newTokenData.user.email,
      name: newTokenData.user.name,
      picture: newTokenData.user.picture,
      role: newTokenData.role,
      permissions: newTokenData.permissions
    });

    // Store the new token
    const stored = storeAuthToken(newToken);
    
    if (!stored) {
      throw new Error('Failed to store refreshed token');
    }

    return getCurrentSession();

  } catch (error) {
    console.error('Failed to refresh session:', error);
    throw error;
  }
};

/**
 * Auto-logout when token expires
 */
export const setupTokenExpirationCheck = (onExpired) => {
  const checkInterval = 60000; // Check every minute

  const intervalId = setInterval(() => {
    const token = getAuthToken();
    
    if (token && !isTokenValid(token)) {
      clearAuthToken();
      onExpired();
      clearInterval(intervalId);
    }
  }, checkInterval);

  return intervalId;
};

/**
 * Validate session and handle automatic cleanup
 */
export const validateSession = () => {
  const session = getCurrentSession();
  
  if (!session) {
    clearAuthToken();
    return null;
  }

  return session;
};

/**
 * Retrieve the Google OAuth access token from the stored session
 * Used to authenticate admin-only API calls (e.g. magic-link generation)
 */
export const getGoogleAccessToken = () => {
  const payload = decodeTokenPayload();
  return payload?.tokens?.access_token || null;
};

/**
 * Initialize session from stored token on app startup
 */
export const initializeSession = () => {
  const token = getAuthToken();
  
  if (!token) {
    return null;
  }

  if (!isTokenValid(token)) {
    clearAuthToken();
    return null;
  }

  return getCurrentSession();
};